/**
 * SEARCH-002 §6 — « Coup d'œil » : lecteur de document PLEIN ÉCRAN ouvert depuis
 * la palette ⌘K, qui SE REFERME pour revenir dans la modale de recherche (jamais
 * de nouvel onglet). Réutilise le mécanisme de visionnage du coffre (URL signée
 * Cellar inline via `gedApi.fetchPreviewUrl` + `getPreviewType`) et cible
 * TOUJOURS la version courante (le `fileId` fourni par l'aperçu, §5.3).
 *
 * Formats : immédiat pour PDF / images / vidéo / audio ; pour les formats Office
 * (MIME non prévisualisable), repli sur « Ouvrir » / « Télécharger » (§6.3).
 */

import { useCallback, useEffect, useState, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Download, ExternalLink, FileWarning, Loader2, X } from 'lucide-react'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import * as gedApi from '@/lib/api/ged'
import { getPreviewType, type PreviewType } from '@/pages/settings/ged/helpers'

interface SearchDocumentQuickLookProps {
    fileId: string | null
    filename: string
    /** MIME — sélectionne le lecteur (PDF/image/vidéo/audio) ou le repli Office. */
    mime: string | null
    entityId: string
    open: boolean
    onOpenChange: (open: boolean) => void
}

export function SearchDocumentQuickLook({
    fileId,
    filename,
    mime,
    entityId,
    open,
    onOpenChange,
}: SearchDocumentQuickLookProps) {
    const { t } = useTranslation()
    const [previewUrl, setPreviewUrl] = useState<string | null>(null)
    const [downloadUrl, setDownloadUrl] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)

    const previewType: PreviewType | null = mime ? getPreviewType(mime) : null

    // Récupère les URLs signées (version courante) à l'ouverture.
    useEffect(() => {
        if (!open || !fileId) {
            setPreviewUrl(null)
            setDownloadUrl(null)
            setError(false)
            return
        }
        let cancelled = false
        setLoading(true)
        setError(false)
        Promise.all([
            gedApi.fetchPreviewUrl(entityId, fileId),
            gedApi.fetchDownloadUrl(entityId, fileId),
        ])
            .then(([preview, download]) => {
                if (cancelled) return
                setPreviewUrl(preview.url)
                setDownloadUrl(download.url)
            })
            .catch(() => {
                if (!cancelled) setError(true)
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [open, fileId, entityId])

    const handleOpen = useCallback(() => {
        if (previewUrl) window.open(previewUrl, '_blank', 'noopener,noreferrer')
    }, [previewUrl])

    const handleDownload = useCallback(() => {
        if (downloadUrl) window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    }, [downloadUrl])

    // Échap referme le coup d'œil → retour modale de recherche (§6.2).
    const onKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation()
                onOpenChange(false)
            }
        },
        [onOpenChange],
    )

    if (!fileId) return null

    const unsupported = !loading && !error && previewUrl && !previewType

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                onKeyDown={onKeyDown}
                hideCloseButton
                className="flex h-[100dvh] max-h-full w-full max-w-full flex-col gap-0 overflow-hidden rounded-none p-0 sm:h-[95vh] sm:max-h-[95vh] sm:w-[95vw] sm:max-w-[95vw] sm:rounded-lg"
            >
                <DialogTitle className="sr-only">{filename}</DialogTitle>

                {/* Barre d'outils : nom + Ouvrir / Télécharger / Fermer */}
                <div className="flex shrink-0 items-center gap-2 border-b border-border bg-background/95 px-3 py-2 backdrop-blur-sm sm:px-4 sm:py-3">
                    <div className="min-w-0 flex-1">
                        <h3 className="truncate text-xs font-semibold sm:text-sm">{filename}</h3>
                        <p className="text-[10px] text-muted-foreground sm:text-xs">
                            {t('globalSearch.preview.provenance.current_version', 'Version à jour')}
                        </p>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleOpen}
                        disabled={!previewUrl}
                        className="h-8 gap-1.5 px-2 text-xs sm:px-3 sm:text-sm"
                    >
                        <ExternalLink className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">{t('globalSearch.preview.actions.open')}</span>
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDownload}
                        disabled={!downloadUrl}
                        className="h-8 gap-1.5 px-2 text-xs sm:px-3 sm:text-sm"
                    >
                        <Download className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">{t('globalSearch.preview.actions.download')}</span>
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => onOpenChange(false)}
                        aria-label={t('common.close', 'Fermer')}
                    >
                        <X className="h-4 w-4" />
                    </Button>
                </div>

                {/* Contenu scrollable */}
                <div className="flex flex-1 items-center justify-center overflow-auto bg-muted/30">
                    {loading && (
                        <div className="flex flex-col items-center gap-3 text-muted-foreground">
                            <Loader2 className="h-8 w-8 animate-spin motion-reduce:animate-none" />
                            <p className="text-sm">{t('ged.preview_loading', 'Chargement…')}</p>
                        </div>
                    )}

                    {error && (
                        <div className="flex flex-col items-center gap-3 text-muted-foreground">
                            <FileWarning className="h-8 w-8" />
                            <p className="text-sm">{t('ged.preview_error', 'Aperçu indisponible.')}</p>
                            <Button variant="outline" size="sm" onClick={handleDownload} disabled={!downloadUrl}>
                                <Download className="mr-2 h-4 w-4" />
                                {t('globalSearch.preview.actions.download')}
                            </Button>
                        </div>
                    )}

                    {!loading && !error && previewUrl && previewType === 'pdf' && (
                        <iframe src={previewUrl} className="h-full w-full border-0" title={filename} />
                    )}

                    {!loading && !error && previewUrl && previewType === 'image' && (
                        <img
                            src={previewUrl}
                            alt={filename}
                            className="max-h-full max-w-full object-contain"
                            draggable={false}
                        />
                    )}

                    {!loading && !error && previewUrl && previewType === 'video' && (
                        <video src={previewUrl} controls className="max-h-full max-w-full rounded-lg" controlsList="nodownload">
                            <track kind="captions" />
                        </video>
                    )}

                    {!loading && !error && previewUrl && previewType === 'audio' && (
                        <audio src={previewUrl} controls className="w-full max-w-md" controlsList="nodownload" />
                    )}

                    {unsupported && (
                        <div className="flex flex-col items-center gap-3 text-muted-foreground">
                            <FileWarning className="h-8 w-8" />
                            <p className="text-sm">{t('ged.preview_unsupported', 'Aperçu indisponible pour ce format.')}</p>
                            <div className="flex gap-2">
                                <Button variant="outline" size="sm" onClick={handleOpen} disabled={!previewUrl}>
                                    <ExternalLink className="mr-2 h-4 w-4" />
                                    {t('globalSearch.preview.actions.open')}
                                </Button>
                                <Button variant="outline" size="sm" onClick={handleDownload} disabled={!downloadUrl}>
                                    <Download className="mr-2 h-4 w-4" />
                                    {t('globalSearch.preview.actions.download')}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}
